// ---------- progression: the player and their badges, then everyone's week ----------
// A generic shell opened from the trophy in the header (index.html, .home-head): a segmented
// switch between "Moi" (src/profile.ts — the player's card and badges, the default tab) and
// "Ensemble" (src/together.ts — the week of all players, no names, no ranks). Neither tab knows
// the other exists: this file only routes between them.
//
// "Ensemble" replaced the weekly league on 2026-10-10. The league ranked players by sheets
// finished this week: the same measure as the Bibliothèque badge, under the same tier names, and
// a reward for volume in a game played to calm down. The reasoning is in
// backend/src/utils/community.ts; GET /me/league stays on the server for older APKs.
//
// Same full-screen pattern as .home (a `.on` class toggle, not the .dlg/aria-hidden sheet
// pattern — cf. src/home.ts openHome()/closeHome()), sitting above the catalogue but below any
// dialog that might one day open on top of it (z-index 25, between .home's 20 and .dlg's 30).

import { celebratePending } from './celebrate';
import { hideMe, showMe } from './profile';
import { setSheetLinks } from './sheetLinks';
import { hideTogether, showTogether, wireTogether } from './together';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

type Tab = 'me' | 'together';
let loaded = false;

function showTab(tab: Tab): void {
  $('progTabMe').classList.toggle('on', tab === 'me');
  $('progTabTogether').classList.toggle('on', tab === 'together');
  if (tab === 'me') {
    hideTogether();
    void showMe($('progressionBody'));
    return;
  }
  hideMe();
  void showTogether($('progressionBody'));
}

export function openProgression(): void {
  $('progression').classList.add('on');
  showTab('me');
  loaded = true;
  // Un palier attendait derrière la pastille : il s'ouvre par-dessus « Moi », tout de suite.
  celebratePending();
}

/** Same contract as closePrefs()/closeUsernameDialog() — what the Android back button chain
 *  needs (src/main.ts) to know whether it consumed the gesture. */
export function closeProgression(): boolean {
  if (!$('progression').classList.contains('on')) return false;
  $('progression').classList.remove('on');
  hideMe();
  hideTogether();
  return true;
}

export function wireProgression(
  openSheet: (id: string) => void,
  inCatalogue: (id: string) => boolean,
  openPacks: (packId: string) => void,
): void {
  // Ouvrir une feuille depuis « Moi » ou « Ensemble » : on referme l'écran d'abord, le catalogue
  // en dessous montre alors le chargement, puis la feuille — ou son message d'erreur.
  setSheetLinks(
    (id) => {
      closeProgression();
      openSheet(id);
    },
    inCatalogue,
    (packId) => {
      closeProgression();
      openPacks(packId);
    },
  );
  $('progressionOpen').onclick = () => openProgression();
  $('progressionBack').onclick = () => closeProgression();
  $('progTabMe').onclick = () => loaded && showTab('me');
  $('progTabTogether').onclick = () => loaded && showTab('together');
  // « Le peindre », sur la ligne du puzzle du jour : on referme l'écran et on passe par l'onglet
  // du calendrier comme si le joueur l'avait touché — le même chemin, donc le même état (le mois
  // rechargé, l'onglet allumé), sans une seconde façon d'ouvrir le calendrier à entretenir.
  wireTogether(() => {
    closeProgression();
    document.querySelector<HTMLElement>('.tab[data-tab="daily"]')?.click();
  });
}
